import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCheckoutStore } from "../store/checkout-store";
import { useDeleteCard, useGetSavedCards } from "@/api/hooks/use-query-api";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";

export const DeletePaymentMethodDialog: React.FC = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const {
    isDeletePaymentMethodDialogOpen,
    deletingPaymentMethodId, // cardToken olarak kullanılıyor
    closeDeletePaymentMethodDialog,
    setSelectedSavedCard,
  } = useCheckoutStore();

  const { data: savedCardsData } = useGetSavedCards();
  const { mutateAsync: deleteCard, isPending: isDeleting } = useDeleteCard();

  // deletingPaymentMethodId aslında cardToken
  const cardToDelete = savedCardsData?.data?.cards?.find(
    (c) => c.cardToken === deletingPaymentMethodId
  );

  const handleDelete = async () => {
    if (!deletingPaymentMethodId) return;
    try {
      await deleteCard(deletingPaymentMethodId);
      success(t("checkout.cardDeletedSuccess"));
      // Silinen kart seçiliyse seçimi temizle
      setSelectedSavedCard(null, null);
      closeDeletePaymentMethodDialog();
    } catch (deleteError: any) {
      error(deleteError?.message || t("checkout.cardDeleteError"));
    }
  };

  if (!cardToDelete) return null;

  return (
    <Dialog
      open={isDeletePaymentMethodDialogOpen}
      onOpenChange={closeDeletePaymentMethodDialog}
    >
      <DialogContent
        open={isDeletePaymentMethodDialogOpen}
        className="max-w-md p-6 rounded-[20px] bg-white dark:bg-gray-900"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[20px] font-semibold leading-[28px] text-[#121926] dark:text-gray-100">
              {t("checkout.deletePaymentMethod")}
            </h2>
            <button
              onClick={closeDeletePaymentMethodDialog}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={isDeleting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-[14px] font-normal leading-[22px] text-[#364152] dark:text-gray-400">
            {t("checkout.deletePaymentMethodConfirmation")}
          </p>

          <div className="bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] p-3 flex items-center gap-3">
            <div className="w-[57px] h-[40px] bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded flex items-center justify-center">
              <PaymentBrandLogo
                brand={(cardToDelete.cardBrand || cardToDelete.cardAssociation || "card").toLowerCase()}
                className="w-10 h-6"
              />
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              {cardToDelete.cardAlias && (
                <p className="text-[12px] font-medium text-[#6172f3]">{cardToDelete.cardAlias}</p>
              )}
              <p className="text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                **** **** **** {cardToDelete.lastFourDigits}
              </p>
              <p className="text-[12px] font-normal text-[#697586] dark:text-gray-400">
                {cardToDelete.cardBankName || cardToDelete.cardBrand}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <Button
            variant="outline"
            onClick={closeDeletePaymentMethodDialog}
            disabled={isDeleting}
            className="flex-1 bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 text-[#414651] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-[8px] px-[14px] py-[10px]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex-1 bg-[#f04438] hover:bg-[#d92d20] dark:bg-[#f04438] dark:hover:bg-[#d92d20] text-white rounded-[10px] px-4 py-[10px] font-semibold text-[14px] leading-[20px]"
          >
            {isDeleting ? t("common.deleting") : t("common.delete")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
